import { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageCircle, Send, Loader2, X } from "lucide-react";

function ChatHeader({ otherUserName, onClose }) {
  return (
    <div className="bg-[#0A2540] px-4 py-2.5 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-[#FACC15]" />
        <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
          {otherUserName || "Conversation"}
        </span>
      </div>
      {onClose && (
        <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="close-chat">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function ChatMessages({ messages, loading, currentUserId }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="h-52 overflow-y-auto px-3 py-2 space-y-2 bg-[#F8FAFC]" data-testid="chat-messages">
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#64748B]" /></div>
      ) : messages.length === 0 ? (
        <p className="text-center text-xs text-[#64748B] py-8">Aucun message. Commencez la conversation !</p>
      ) : (
        messages.map((m) => {
          const isMine = m.sender_id === currentUserId;
          return (
            <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-xl px-3 py-2 ${isMine ? "bg-[#0A2540] text-white" : "bg-white border border-[#CBD5E1] text-[#0A2540]"}`}>
                {!isMine && <p className="text-[10px] font-semibold mb-0.5 text-[#FACC15]">{m.sender_name}</p>}
                <p className="text-xs leading-relaxed">{m.content}</p>
                <p className={`text-[9px] mt-0.5 ${isMine ? "text-white/50" : "text-[#64748B]"}`}>
                  {m.created_at?.split("T")[1]?.slice(0, 5)}
                </p>
              </div>
            </div>
          );
        })
      )}
      <div ref={bottomRef} />
    </div>
  );
}

function ChatInput({ onSend, sending }) {
  const [input, setInput] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    onSend(input.trim());
    setInput("");
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 p-2 border-t border-[#CBD5E1]">
      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Ecrire un message..."
        className="h-9 border-[#CBD5E1] text-sm flex-1"
        data-testid="chat-input"
      />
      <Button type="submit" size="sm" disabled={sending || !input.trim()} className="bg-[#0A2540] text-white hover:bg-[#081b2e] h-9 px-3" data-testid="send-message-btn">
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
      </Button>
    </form>
  );
}

export default function ChatPanel({ bookingId, currentUserId, otherUserName, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/booking/${bookingId}`);
      setMessages(data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Chat fetch error:", e.message);
    }
  }, [bookingId]);

  useEffect(() => {
    setLoading(true);
    fetchMessages().finally(() => setLoading(false));
    pollRef.current = setInterval(fetchMessages, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchMessages]);

  const sendMessage = async (content) => {
    setSending(true);
    try {
      await api.post("/messages", { booking_id: bookingId, content });
      fetchMessages();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setSending(false); }
  };

  return (
    <div className="border border-[#CBD5E1] rounded-xl bg-white overflow-hidden" data-testid={`chat-panel-${bookingId}`}>
      <ChatHeader otherUserName={otherUserName} onClose={onClose} />
      <ChatMessages messages={messages} loading={loading} currentUserId={currentUserId} />
      <ChatInput onSend={sendMessage} sending={sending} />
    </div>
  );
}
